import { Toggle } from '@fashionnovateam/nova-ui-toolkit';
import React, { useState } from 'react';
import {
  FormErrors,
  PurchaseOrderRevisionForm,
  UpdatePurchaseOrderRevisionForm,
} from './validation';
import {
  PurchaseOrder,
  PurchaseOrderLine,
} from '../../../Lib/Types/purchase-order';
import { QuantitiesForm } from './QuantitiesForm';
import { PricesForm } from './PricesForm';
import { ItemCommentForm } from './ItemCommentForm';
import { ErrorWithIcon } from './ErrorWithIcon';

export type ItemFormProps = {
  purchaseOrder: PurchaseOrder;
  lines: PurchaseOrderLine[];
  styleColor: string;
  name: string;
  form: PurchaseOrderRevisionForm;
  updateForm: UpdatePurchaseOrderRevisionForm;
  disabled: boolean;
  errors: FormErrors | undefined;
};

function Separator() {
  return <div className="w-full h-[1px] border-neutral-100 border-b" />;
}

export function ItemForm(props: ItemFormProps) {
  const { lines, styleColor, name, form, updateForm, disabled, errors } = props;
  const [formPrevValue, setFormPrevValue] = useState<
    NonNullable<PurchaseOrderRevisionForm['items']>[number] | undefined
  >(undefined);

  const item = form?.items?.find((i) => i.style_color === styleColor);
  const index = form?.items?.findIndex((i) => i.style_color === styleColor);
  const jrCost = lines.find((line) => line.size_type === 'Junior')?.price;
  const plusCost = lines.find((line) => line.size_type === 'Plus')?.price;

  return (
    <div className="flex flex-col">
      <div
        className="flex items-center gap-3"
        data-testid={`revision-item-row-${styleColor}`}
      >
        <Toggle
          onChange={(e) =>
            updateForm((prev) => {
              if (e.target.checked) {
                return {
                  ...prev,
                  items: [
                    ...(prev.items || []),
                    formPrevValue || {
                      style_color: styleColor,
                    },
                  ],
                };
              }
              const currentItem = prev.items?.find(
                (i) => i.style_color === styleColor,
              );
              setFormPrevValue(currentItem);
              const newArray = prev.items?.filter(
                (i) => i.style_color !== styleColor,
              );
              return {
                ...prev,
                items: newArray?.length ? newArray : undefined,
              };
            })
          }
          checked={!!item}
          disabled={disabled}
        />
        <div className="flex flex-col">
          <span className="text-sm font-medium text-base-primary">{name}</span>
          <span className="text-xsm font-normal text-base-disabled">
            {styleColor}
          </span>
        </div>
      </div>
      {item && index !== undefined && (
        <div className="flex flex-col gap-5 p-5 my-5 border border-neutral-100 rounded-lg shadow-[0px_2px_4px_0px_#0000000A]">
          <QuantitiesForm item={item} index={index} {...props} />
          {(jrCost || plusCost) && (
            <>
              <Separator />
              <PricesForm
                jrCost={jrCost}
                plusCost={plusCost}
                item={item}
                index={index}
                {...props}
              />
            </>
          )}
          <Separator />
          <ItemCommentForm item={item} index={index} {...props} />
          {errors?.[`items.${index}`] && (
            <ErrorWithIcon error={errors?.[`items.${index}`]} />
          )}
        </div>
      )}
    </div>
  );
}
